import * as React from 'react';
import { Dialog, DialogTitle, DialogContent, Button, DialogActions, Table, TableHead, TableRow, TableBody, TableCell, TablePagination, Checkbox, TableSortLabel, Tabs, Tab, AppBar, Typography, Paper, TableContainer, TextField, InputAdornment } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { IAuthorizationRelationData } from './AuthorizationDomainModel';
import LoadingIcon from '../../components/LoadingIcon';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ALERT, IIdNamePair } from '../../services/ResourceRelationsService';
import Moment from "moment";
import { IUserAuthorizationDataRow, IUserAuthorizationData, IUserAuthorizationLoadingData } from './AuthUserAuthorizationsService';
import lang from '../Relations/RelationsLang';
import { OptionType } from '../EmployeesComboboxMI';
import clsx from 'clsx';
import { IConfig } from './Models';
import useDebounce from '../../utils/useDebounce';
import { useTranslation } from 'react-i18next';

import "./UserAuthorizationsWindow.css";

function desc<T>(a: T, b: T, orderBy: keyof T) {

	let aValue = columnToString(orderBy as string, a as any) ?? "";
	let bValue = columnToString(orderBy as string, b as any) ?? "";

	return aValue.localeCompare(bValue);
}

function stableSort<T>(array: T[], cmp: (a: T, b: T) => number) {
	const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
	stabilizedThis.sort((a, b) => {
		const order = cmp(a[0], b[0]);
		if (order !== 0) return order;
		return a[1] - b[1];
	});
	return stabilizedThis.map(el => el[0]);
}

type Order = 'asc' | 'desc';

function getSorting<K extends keyof any>(
	order: Order,
	orderBy: K,
): (a: { [key in K]: number | string | boolean }, b: { [key in K]: number | string  }) => number {
	return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

function columnToString(columnId: string, row: IUserAuthorizationDataRow): string {

	switch (columnId) {
		case "period":
			return parseDate(row.inceptionDate) + "|" + parseDate(row.expirationDate);
		case "inceptionDate":
			return parseDate(row.inceptionDate);
		case "expirationDate":
			return parseDate(row.expirationDate);
	};

	if (typeof row[columnId] === 'string')
		return row[columnId];

	return null;
}

function search(searchText: string, searchedColumns: string[], data: IUserAuthorizationDataRow): boolean {
	if (!searchText) {
		return true;
	}

	for (let i = 0; i < searchedColumns.length; i++) {
		let searchedColumn = searchedColumns[i];
		let columnText = columnToString(searchedColumn, data);

		if (!columnText) {
			continue;
		}

		if (columnText.toLocaleLowerCase().indexOf(searchText) >= 0)
			return true;
	}

	return false;
}

interface EnhancedTableProps {
	classes: ReturnType<typeof useStyles>;
	numSelected: number;
	onRequestSort: (event: React.MouseEvent<unknown>, property: keyof IUserAuthorizationDataRow) => void;
	onSelectAllClick: (checked: boolean) => void;
	order: Order;
	orderBy: string;
	rowCount: number;

	canSelect: boolean,
	config: IUserAuthorizationsWindowConfig
}

export interface IUserAuthorizationsWindowConfig {
	filters: string[],
	columns: HeadRow[];
}

interface HeadRow {
	disablePadding: boolean;
	id: keyof IUserAuthorizationDataRow;
	labelPl: string;
	labelEn?: string;
	labelDe?: string;
	labelEs?: string;
	className?: string;
	minWidth?: number;
	searched: boolean;
}

const defaultConfig: IUserAuthorizationsWindowConfig = {
	filters: [],
	columns: [
		{ id: 'name', disablePadding: true, labelPl: lang.resource(), minWidth: 230, searched: true },
		{ id: 'period', disablePadding: false, labelPl: lang.period(), minWidth: 170, searched: false },
		{ id: 'authorizationLevel', disablePadding: false, labelPl: lang.authorizationLevel(), searched: true },
		{ id: 'relationStatus', disablePadding: false, labelPl: 'Status', searched: false  },
	]
};

function EnhancedTableHead(props: EnhancedTableProps) {
	const { classes, onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
	const createSortHandler = (property: keyof IUserAuthorizationDataRow) => (event: React.MouseEvent<unknown>) => {
		onRequestSort(event, property);
	};

	return (
		<TableHead>
			<TableRow>
				{props.canSelect && <TableCell padding="checkbox">
					<Checkbox
						color="primary"
						indeterminate={numSelected > 0 && numSelected < rowCount}
						checked={numSelected === rowCount}
						onClick={() => onSelectAllClick(numSelected === rowCount)}
						inputProps={{ 'aria-label': 'select all desserts' }}
					/>
				</TableCell>}
				{props.config.columns.map(column => (
					<TableCell
						key={column.id}
						padding={column.disablePadding ? 'none' : 'default'}
						sortDirection={orderBy === column.id ? order : false}
						className={clsx("table-header", classes.headerCell, column.className)}
						style={{ minWidth: column.minWidth }}
					>
						<TableSortLabel
							active={orderBy === column.id}
							direction={order}
							onClick={createSortHandler(column.id)}
						>
							{lang.select(column.labelPl, column.labelEn, column.labelDe, column.labelEs)}
							{orderBy === column.id ? (
								<span className={classes.visuallyHidden}>
									{order === 'desc' ? 'sorted descending' : 'sorted ascending'}
								</span>
							) : null}
						</TableSortLabel>
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

function parseDate(isoDate: string): string {
	const qdesk = (window as any).qdesk;
	let date: string = null;
	if (isoDate)
		date = qdesk.moment.format(isoDate, qdesk.settings.dateFormat);
	return date;
}

interface ITableCellProps {
	row: IUserAuthorizationDataRow
	column: HeadRow
}

function TableCellDisplay(props: ITableCellProps) {
	const { column, row } = props;

	switch (column.id) {
		
		case "period":
			return <>{parseDate(row.inceptionDate)} &mdash; {parseDate(row.expirationDate)}</>
		case "inceptionDate":
			return <>{parseDate(row.inceptionDate)}</>
		case "expirationDate":
			return <>{parseDate(row.expirationDate)}</>

		default:
			return <>{row[column.id]}</>
	};
}

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			width: '100%',
			marginTop: theme.spacing(3),
		},
		container: {
			maxHeight: 'calc(100vh - 300px)',
		},
		paper: {
			width: '100%',
			marginBottom: theme.spacing(2),
		},
		table: {
		},
		headerCell: {
			whiteSpace: 'nowrap',
		},
		tableCell: {
			whiteSpace: 'nowrap',
		},
		visuallyHidden: {
			border: 0,
			clip: 'rect(0 0 0 0)',
			height: 1,
			margin: -1,
			overflow: 'hidden',
			padding: 0,
			position: 'absolute',
			top: 20,
			width: 1,
		},
	}), {
		name: "UserAuthorizationsWindow",
	}
);

const isSelected = (realtionId: number, selected: number[]) => selected.indexOf(realtionId) !== -1;

interface IPropsTable {
	rowsPerUser: IUserAuthorizationDataRow[];
	canSelect: boolean;
	blockedRelationIds: () => number[];
	selected?: number[];
	setSelected?: (selecetd: number[]) => void;

	config: IUserAuthorizationsWindowConfig;

	search: string;
}


export interface ITableRefApi {
	resetPage: () => void,
}

export const UserAuthorizationsTable = React.forwardRef((props: IPropsTable, ref) => {
	
	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<keyof IUserAuthorizationDataRow>('name');
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(10);

	const [config, setConfig] = React.useState<IUserAuthorizationsWindowConfig>(defaultConfig);
	const [rows, setRows] = React.useState<IUserAuthorizationDataRow[]>(props.rowsPerUser ?? []);

	React.useEffect(() => {
		setPage(0);
		const rowsPerUser = props.rowsPerUser ?? [];
		if (!props.search) {
			setRows(rowsPerUser);
			return;
		}

		var searchedColumns = config.columns.filter(x => x.searched).map(x => x.id);
		let searchText = props.search.toLowerCase().trim();
		setRows(rowsPerUser.filter(x => search(searchText, searchedColumns, x)));

	}, [config, props.rowsPerUser, props.search]);

	React.useEffect(() => {
		setConfig({ ...defaultConfig, ...props.config });
	}, [props.config])

	React.useImperativeHandle(ref, () => {
		let api: ITableRefApi = {
			resetPage: () => setPage(0),
		}
		return api;
	});

	function handleRequestSort(event: React.MouseEvent<unknown>, property: keyof IUserAuthorizationDataRow) {
		const isDesc = orderBy === property && order === 'desc';
		setOrder(isDesc ? 'asc' : 'desc');
		setOrderBy(property);
	}

	function handleSelectAllClick(checked: boolean) {
		if (!checked) {
			const newSelecteds = rows.map(n => n.relationId);
			if (props.setSelected)
				props.setSelected(newSelecteds);
			return;
		}
		if (props.setSelected)
			props.setSelected([]);
	}

	function handleClick(event: React.MouseEvent<unknown>, row: IUserAuthorizationDataRow) {

		if (!props.canSelect) {
			return;
		}

		const selectedIndex = props.selected.indexOf(row.relationId);

		let newSelected: number[] = [];

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(props.selected, row.relationId);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(props.selected.slice(1));
		} else if (selectedIndex === props.selected.length - 1) {
			newSelected = newSelected.concat(props.selected.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				props.selected.slice(0, selectedIndex),
				props.selected.slice(selectedIndex + 1),
			);
		}
		if (props.setSelected)
			props.setSelected(newSelected);
	}

	function handleChangePage(event: unknown, newPage: number) {
		setPage(newPage);
	}

	function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
		setRowsPerPage(+event.target.value);
		setPage(0);
	}

	if (!props.rowsPerUser)
		return <LoadingIcon />;

	let blockedRelationIds = props.blockedRelationIds();
	const isBlocked = (realtionId: number) => blockedRelationIds.indexOf(realtionId) !== -1;

	const classes = useStyles({});

	return <Paper className={classes.paper}>
		<TableContainer className={classes.container} >
			<Table
				stickyHeader
				className={classes.table}
			>
				<EnhancedTableHead
					classes={classes}
					numSelected={props.selected?.length ?? 0}
					order={order}
					orderBy={orderBy}
					onSelectAllClick={handleSelectAllClick}
					onRequestSort={handleRequestSort}
					rowCount={rows.length}
					canSelect={props.canSelect}
					config={config}
				/>
				<TableBody>
					{stableSort(rows, getSorting(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
						.map((row: IUserAuthorizationDataRow, index) => {

							let isItemBlocked = isBlocked(row.relationId);
							let isItemSelected = true;
							if (!isItemBlocked && props.selected) {
								isItemSelected = isSelected(row.relationId, props.selected);
							}

							if (!props.canSelect) {
								isItemSelected = false;
							}

							return (<TableRow
								hover
								onClick={event => !isItemBlocked && handleClick(event, row)}
								role="checkbox"
								tabIndex={-1}
								key={row.relationId}
								selected={isItemSelected}
								className={clsx({
									"row-selected": isItemSelected
								})}
							>
								{props.canSelect && <TableCell padding="checkbox">
									<Checkbox
										className="mi-checkbox-input"
										disabled={isItemBlocked}
										color="primary"
										checked={isItemSelected}
									/>
								</TableCell>}

								{config.columns.map(column => (
									<TableCell
										key={column.id}
										padding={column.disablePadding ? 'none' : 'default'}
										className={clsx(classes.tableCell, "table-row", column.className)}
									>
										<TableCellDisplay column={column} row={row} />
									</TableCell>
								))}
							</TableRow>)
						})}
				</TableBody>
			</Table>
				
		</TableContainer>
		<TablePagination
			rowsPerPageOptions={[10, 25, 50]}
			component="div"
			count={rows.length}
			rowsPerPage={rowsPerPage}
			page={page}
			backIconButtonProps={{
				'aria-label': 'previous page',
			}}
			nextIconButtonProps={{
				'aria-label': 'next page',
			}}
			onChangePage={handleChangePage}
			onChangeRowsPerPage={handleChangeRowsPerPage}
			labelRowsPerPage="Wierszy na stronie:"
			labelDisplayedRows={(paginagionInfo) => {
				return <span>{paginagionInfo.from}
					{"-"}
					{paginagionInfo.to}
					{" z "} {paginagionInfo.count}
				</span>
			}}
		/>
	</Paper>
});

interface TabPanelProps {
	children?: React.ReactNode;
	index: any;
	value: any;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<Typography
			component="div"
			role="tabpanel"
			hidden={value !== index}
			id={`scrollable-force-tabpanel-${index}`}
			aria-labelledby={`scrollable-force-tab-${index}`}
			{...other}
		>
			{children}
		</Typography>
	);
}

interface IUserAuthorizationsDialogProps {
	user: OptionType,
	canInsert: boolean,
	onInsert: (items: IAuthorizationRelationData[]) => void,
	rowsPerUser: IUserAuthorizationData,
	loading: IUserAuthorizationLoadingData,
	blockedRelationIds: () => number[],
	getLevelByName: (name: string) => IIdNamePair,

	tableConfig: IUserAuthorizationsWindowConfig,
	config: IConfig,

	open: boolean,
	onClosed: () => void,
}

export function UserAuthorizationsDialog(props: IUserAuthorizationsDialogProps) {
	const { t } = useTranslation();
	const [tab, setTab] = React.useState(hasDataSet() ? 0 : 1);

	const [search, setSearch] = React.useState("");
	const [searchLazy, setSearchLazy] = React.useState("");
	const debouncedSearchTerm = useDebounce(search, 1500);

	const [selectedDataSets, setSelectedDataSets] = React.useState<number[]>([]);
	const [selectedBussiness, setSelectedBussiness] = React.useState<number[]>([]);

	const selected1Ref = React.useRef<ITableRefApi>();
	const selected2Ref = React.useRef<ITableRefApi>();

	React.useEffect(() => {
		setSearchLazy(search);
	}, [debouncedSearchTerm]);

	React.useEffect(() => {
		if (props.open) {
			selected1Ref.current?.resetPage();
			selected2Ref.current?.resetPage();
		}
	}, [props.open]);


	function handleClose() {
		props.onClosed();
	}

	const handleTabChange = (event, newValue) => {
		setTab(newValue);
	};

	function hasDataSet(): boolean {
		return !props.config.fields.dataSet.hidden;
	}

	function hasDataSetAndBusinessProcess(): boolean {
		return hasDataSet() && hasBusinessProcess();
	}

	function hasBusinessProcess() {
		return !props.config.fields.businessProcess.hidden;
	}

	function parseRow(row: IUserAuthorizationDataRow): IAuthorizationRelationData {
		if (isSelected(row.relationId, allSelected())) {

			let from = null;
			let to = null;

			if (row.inceptionDate)
				from = Moment(row.inceptionDate)
			if (row.expirationDate)
				to = Moment(row.expirationDate);

			return {
				alert: ALERT.Access,
				employee: {
					id: parseInt(props.user.value),
					name: props.user.label,
					kind: 'Employee',
					label: props.user.label,
					type: null,
				},
				resource: {
					id: row.id,
					label: row.name,
					kind: row.kind,
				},
				from: from,
				to: to,
				relationId: row.relationId,
				level: props.getLevelByName(row.authorizationLevel),
				deleted: false,
				id: null,
				key: null,
				errorMessages: [],
			}
		}

		return null;
	}

	function handleAddSelected() {
		
		let items: IAuthorizationRelationData[] = [];
		if (props.rowsPerUser.forBusinessProcess) {
			for (let i = 0; i < props.rowsPerUser.forBusinessProcess.length; i++) {
				let row = props.rowsPerUser.forBusinessProcess[i] as IUserAuthorizationDataRow;

				var item = parseRow(row);
				if (item)
					items.push(item);
			}
		}
		if (props.rowsPerUser.forDataSet) {
			for (let i = 0; i < props.rowsPerUser.forDataSet.length; i++) {
				let row = props.rowsPerUser.forDataSet[i] as IUserAuthorizationDataRow;

				var item = parseRow(row);
				if (item)
					items.push(item);
			}
		}
		props.onInsert(items);
	}

	function allSelected(): number[] {
		var result = [];

		result = result.concat(selectedDataSets);
		result = result.concat(selectedBussiness);

		return result;
	}

	return (
		<Dialog
			fullWidth
			maxWidth="lg"
			open={props.open}
			onClose={handleClose}
			className="user-authorization-window-dialog"
		>
			<DialogTitle>
				<TextField
					variant="outlined"
					placeholder={lang.get("search") + "..."}

					value={search}
					onChange={(e) => setSearch(e.target.value)}
					size="small"
					style={{
						float: "right"
					}}
					InputProps={{
						startAdornment: <InputAdornment position="start" > <SearchIcon color="disabled" /></InputAdornment>
					}}
				/>

				{lang.get("authorizationFor")}: {props.user.label}
			</DialogTitle>
			<DialogContent>
				{props.rowsPerUser?.forDataSetStatus && props.rowsPerUser?.forDataSetStatus !== 'Success' && <Typography color="error">
					{t("resources.authorizations.resultStatus." + props.rowsPerUser.forDataSetStatus)}
				</Typography>}

				{hasDataSetAndBusinessProcess() && <AppBar position="static" color="default">
					<Tabs
						value={tab}
						indicatorColor="primary"
						textColor="primary"
						onChange={handleTabChange}
					>
						<Tab label={lang.dataSets()} />
						<Tab label={lang.businessProcesses()} />
					</Tabs>
				</AppBar>}
				{hasDataSet() && props.rowsPerUser?.forDataSet && <TabPanel value={tab} index={0}>
					<UserAuthorizationsTable
						blockedRelationIds={props.blockedRelationIds}
						canSelect={props.canInsert}
						rowsPerUser={props.rowsPerUser?.forDataSet}
						ref={selected1Ref}
						selected={selectedDataSets}
						setSelected={(s) => setSelectedDataSets(s)}
						config={props.tableConfig}
						search={searchLazy}
					/>
				</TabPanel>}
				{hasBusinessProcess() && props.rowsPerUser?.forBusinessProcess && <TabPanel value={tab} index={1}>
					<UserAuthorizationsTable
						blockedRelationIds={props.blockedRelationIds}
						canSelect={props.canInsert}
						rowsPerUser={props.rowsPerUser?.forBusinessProcess}
						ref={selected2Ref}
						selected={selectedBussiness}
						setSelected={(s) => setSelectedBussiness(s)}
						config={props.tableConfig}
						search={searchLazy}
					/>
				</TabPanel>}
			</DialogContent>
			<DialogActions>
				{props.canInsert && <Button type="button" onClick={handleAddSelected} color="primary" disabled={allSelected().length == 0}>
					{lang.addToIssue()}
				</Button>}

				<Button type="button" onClick={handleClose} autoFocus>
					{lang.get("close")}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

interface IProps {
	user: OptionType,
	canInsert: boolean,
	onInsert: (items: IAuthorizationRelationData[]) => void,
	rowsPerUser: IUserAuthorizationData,
	loading: IUserAuthorizationLoadingData,
	blockedRelationIds: () => number[],
	getLevelByName: (name: string) => IIdNamePair,

	tableConfig: IUserAuthorizationsWindowConfig,
	config: IConfig,
}

export default function UserAuthorizationsWindow(props: IProps) {
	
	const [open, setOpen] = React.useState(false);

	function handleClickOpen() {
		setOpen(true);
	}

	function handleClose() {
		setOpen(false);
	}

	function handleInsert(items: IAuthorizationRelationData[]) {
		setOpen(false);
		if (items && items.length > 0)
			props.onInsert(items);
	}

	if (!props.user?.value)
		return null;

	return (
		<div className="user-accesses-window">
			<button type="button" className="qd-btn qd-btn-default" onClick={handleClickOpen}>
				{lang.currentAuthorizations()}
			</button>
			<UserAuthorizationsDialog
				open={open}
				onClosed={handleClose}
				onInsert={handleInsert}
				blockedRelationIds={props.blockedRelationIds}
				canInsert={props.canInsert}
				config={props.config}
				getLevelByName={props.getLevelByName}
				loading={props.loading}
				rowsPerUser={props.rowsPerUser}
				tableConfig={props.tableConfig}
				user={props.user}
			/>
		</div>
	);
}